import React, { useState, useEffect } from 'react';
import { Radio, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';

const PLACEHOLDER_LIGHT = '/assets/placeholders/video_default_light.jpg';

const LiveSection_Conditional = React.memo(({
  isLive, 
  liveData, 
  scheduledLive,
  onWatchLive,
  loading = false 
}) => {
  const { isDark } = useTheme();
  const [countdown, setCountdown] = useState('');
  
  const showRealLive = isLive && liveData?.live_videoId;
  const now = new Date();
  const isScheduledTime = scheduledLive?.enabled && 
    scheduledLive?.startTime && 
    scheduledLive?.endTime &&
    now >= new Date(scheduledLive.startTime) && 
    now <= new Date(scheduledLive.endTime);
  
  const showScheduledLive = !showRealLive && isScheduledTime;
  
  useEffect(() => {
    if (!scheduledLive?.startTime || showRealLive) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const start = new Date(scheduledLive.startTime);
      const diff = start - now;
      
      if (diff <= 0) {
        setCountdown('');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (days > 0) {
        setCountdown(`${days}j ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setCountdown(`${minutes}m ${seconds}s`);
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [scheduledLive?.startTime, showRealLive]);

  if (loading) {
    return (
      <div className="py-8 px-6 bg-gradient-to-r from-gray-700 to-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto" />
          <p className="text-white mt-2">Vérification du direct...</p>
        </div>
      </div>
    );
  }
  
  if (!showRealLive && !showScheduledLive) {
    return null;
  }

  if (showRealLive) {
    const embedUrl = liveData.embedUrl || `https://www.youtube.com/embed/${liveData.live_videoId}?autoplay=1`;
    
    return (
      <div className="py-10 px-6 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-white px-5 py-2.5 rounded-full shadow-lg animate-pulse">
              <Radio size={20} className="text-red-600" />
              <span className="text-red-600 font-bold uppercase tracking-wider">EN DIRECT</span>
            </div>
          </div>

          <Card className={`overflow-hidden rounded-2xl shadow-2xl ${isDark ? 'border-[#444444]' : ''}`}>
            <CardContent className="p-0">
              <div className="relative aspect-video bg-black">
                <iframe
                  src={embedUrl}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={liveData.live_title || 'Culte en direct'}
                />
              </div>

              <div className={`p-6 ${isDark ? 'bg-[#333333]' : 'bg-white'}`}>
                <h3 className={`text-2xl font-bold mb-2 tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>
                  {liveData.live_title || 'Culte en direct'}
                </h3>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Rejoignez-nous maintenant pour ce moment de partage
                </p>

                <Button
                  onClick={onWatchLive}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold w-full py-3 rounded-xl shadow-md"
                  data-testid="watch-live-btn"
                >
                  Regarder en direct
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showScheduledLive) {
    return (
      <div className="py-10 px-6 bg-gradient-to-r from-pcc-accent to-pcc-accent-dark">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-white px-5 py-2.5 rounded-full shadow-lg">
              <CalendarIcon size={20} className="text-pcc-accent" />
              <span className="text-pcc-accent font-bold uppercase tracking-wider">DIRECT PROGRAMMÉ</span>
            </div>
          </div>

          <Card className={`overflow-hidden rounded-2xl shadow-2xl ${isDark ? 'border-[#444444]' : ''}`}>
            <CardContent className="p-0">
              <div className="relative aspect-video bg-gray-900">
                <img loading="lazy"
                  src={PLACEHOLDER_LIGHT}
                  alt="Direct programmé"
                  className="w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Radio size={48} className="text-white mb-4" />
                  {countdown && (
                    <div className="text-center">
                      <p className="text-white text-lg mb-2">Le direct commence dans</p>
                      <p className="text-4xl font-bold text-pcc-accent">{countdown}</p>
                    </div>
                  )}
                  {!countdown && (
                    <p className="text-white text-lg">Le direct va bientôt commencer...</p>
                  )}
                </div>
              </div>

              <div className={`p-6 ${isDark ? 'bg-[#333333]' : 'bg-white'}`}>
                <h3 className={`text-2xl font-bold mb-2 tracking-tight ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>
                  {scheduledLive.title || 'Culte en direct'}
                </h3>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {scheduledLive.description || "Rejoignez-nous pour un moment de louange et d'enseignement"}
                </p>
                
                <Button
                  onClick={onWatchLive}
                  className="bg-pcc-accent hover:bg-pcc-accent-dark text-black font-semibold w-full py-3 rounded-xl shadow-md"
                  data-testid="scheduled-live-btn"
                >
                  Définir un rappel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
});

export default LiveSection_Conditional;
